import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Upload, Image as ImageIcon, Edit2, Trash2, ZoomIn, ZoomOut, RotateCw, X as CloseIcon, ChevronLeft, ChevronRight, LayoutGrid, List } from 'lucide-react';
import { format } from 'date-fns';
import { uploadFittingPhoto, getFittingPhotos, deleteFittingPhoto, updateFittingPhoto, rotateFittingPhoto } from '@/lib/api';

const PHOTO_TYPES = [
  { value: 'initial_measurement', label: 'Initial Measurement' },
  { value: 'fitting_session', label: 'Fitting Session' },
];

const VIEW_TYPES = [
  { value: 'front', label: 'Front View' },
  { value: 'back', label: 'Back View' },
  { value: 'side', label: 'Side View' },
  { value: 'all_sides', label: 'All Sides' },
];

const PhotosTab = ({ customerId, customer, patterns, onRefresh }) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPatternFilterIds, setSelectedPatternFilterIds] = useState([]);
  const [dateFilterMode, setDateFilterMode] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [galleryMode, setGalleryMode] = useState('grid');
  const [gridDensity, setGridDensity] = useState('small');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [viewerPhotoIndex, setViewerPhotoIndex] = useState(0);
  const [viewerSaving, setViewerSaving] = useState(false);
  const [viewerPendingRotationTurns, setViewerPendingRotationTurns] = useState(0);
  const [viewerEditForm, setViewerEditForm] = useState({
    photo_type: '',
    view_type: '',
    date: '',
    description: '',
    pattern_ids: [],
  });
  const [pendingRotationTurns, setPendingRotationTurns] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const [editForm, setEditForm] = useState({ 
    photo_type: '', 
    view_type: '', 
    date: '', 
    description: '',
    pattern_ids: [],
  });
  const [photoForm, setPhotoForm] = useState({
    photo_type: 'fitting_session',
    date: new Date().toISOString().split('T')[0],
    description: '',
    pattern_ids: [],
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const resolvePhotoUrl = (uri) => {
    if (!uri) return '';
    if (uri.startsWith('http')) return uri;
    return `${backendUrl}${uri.replace('/uploads/', '/api/uploads/')}`;
  };

  const getPhotoPatternIds = (photo) => {
    if (Array.isArray(photo?.pattern_ids) && photo.pattern_ids.length > 0) {
      return photo.pattern_ids.filter(Boolean);
    }
    return photo?.pattern_id ? [photo.pattern_id] : [];
  };

  const extractPhotoDate = (photo) => {
    const taggedDate = (photo?.tags || []).find((tag) => tag.match(/^\d{4}-\d{2}-\d{2}$/));
    if (taggedDate) return taggedDate;
    return (photo?.created_at || '').slice(0, 10);
  };

  const buildEditFormFromPhoto = (photo) => {
    const tags = photo?.tags || [];
    const photoTypeValue = tags.find(t => ['initial_measurement', 'fitting_session'].includes(t)) ||
      tags.find(t => !['front', 'back', 'side', 'all_sides'].includes(t) && !t.match(/^\d{4}-\d{2}-\d{2}$/)) ||
      '';
    const viewTypeValue = tags.find(t => ['front', 'back', 'side', 'all_sides'].includes(t)) || '';
    const dateTag = tags.find(t => t.match(/^\d{4}-\d{2}-\d{2}$/)) || new Date().toISOString().split('T')[0];

    return {
      photo_type: PHOTO_TYPES.find(t => t.value === photoTypeValue)?.label || photoTypeValue,
      view_type: VIEW_TYPES.find(t => t.value === viewTypeValue)?.label || viewTypeValue,
      date: dateTag,
      description: photo?.description || '',
      pattern_ids: getPhotoPatternIds(photo),
    };
  };

  const patternCodeById = useMemo(() => {
    const map = {};
    (patterns || []).forEach((pattern) => {
      if (pattern?.pattern_id) {
        map[pattern.pattern_id] = pattern.pattern_code || pattern.pattern_id;
      }
    });
    return map;
  }, [patterns]);

  const patternFilterOptions = useMemo(() => {
    const counts = {};
    photos.forEach((photo) => {
      getPhotoPatternIds(photo).forEach((patternId) => {
        counts[patternId] = (counts[patternId] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .map(([patternId, count]) => ({
        patternId,
        label: patternCodeById[patternId] || patternId,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [photos, patternCodeById]);

  const quickUploadDateOptions = useMemo(() => {
    const uniqueDates = [];
    photos.forEach((photo) => {
      const normalizedDate = extractPhotoDate(photo);
      if (normalizedDate && !uniqueDates.includes(normalizedDate)) {
        uniqueDates.push(normalizedDate);
      }
    });
    return uniqueDates.sort((a, b) => b.localeCompare(a)).slice(0, 7);
  }, [photos]);

  const filteredPhotos = useMemo(() => {
    return photos.filter((photo) => {
      const patternIds = getPhotoPatternIds(photo);
      const photoDate = extractPhotoDate(photo);

      const patternMatch = selectedPatternFilterIds.length === 0 ||
        selectedPatternFilterIds.some((patternId) => patternIds.includes(patternId));

      let dateMatch = true;
      if (dateFilterMode.startsWith('date:')) {
        dateMatch = photoDate === dateFilterMode.replace('date:', '');
      } else if (dateFilterMode === 'custom') {
        if (customDateFrom && photoDate < customDateFrom) dateMatch = false;
        if (customDateTo && photoDate > customDateTo) dateMatch = false;
      }

      return patternMatch && dateMatch;
    });
  }, [photos, selectedPatternFilterIds, dateFilterMode, customDateFrom, customDateTo]);

  const gridClassName = useMemo(() => {
    if (gridDensity === 'large') return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4';
    if (gridDensity === 'medium') return 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4';
    return 'grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3';
  }, [gridDensity]);

  const groupedPhotos = useMemo(() => {
    const groups = {};
    filteredPhotos.forEach((photo) => {
      const photoPatternIds = getPhotoPatternIds(photo);
      const primaryPatternId = photoPatternIds[0] || 'unassigned';
      const primaryLabel = primaryPatternId === 'unassigned'
        ? 'Unassigned'
        : (photo.pattern_codes?.[0] || patternCodeById[primaryPatternId] || primaryPatternId);

      if (!groups[primaryPatternId]) {
        groups[primaryPatternId] = {
          key: primaryPatternId,
          label: primaryLabel,
          photos: [],
        };
      }
      groups[primaryPatternId].photos.push(photo);
    });

    return Object.values(groups).sort((a, b) => {
      if (a.key === 'unassigned') return 1;
      if (b.key === 'unassigned') return -1;
      return a.label.localeCompare(b.label);
    });
  }, [filteredPhotos, patternCodeById]);

  // Fetch photos
  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const response = await getFittingPhotos(customerId, null, null);
      setPhotos(response.data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    const validPatternIds = new Set(patternFilterOptions.map((option) => option.patternId));
    setSelectedPatternFilterIds((prev) => prev.filter((patternId) => validPatternIds.has(patternId)));
  }, [patternFilterOptions]);

  useEffect(() => {
    if (!dateFilterMode.startsWith('date:')) return;
    const selectedDate = dateFilterMode.replace('date:', '');
    if (!quickUploadDateOptions.includes(selectedDate)) {
      setDateFilterMode('all');
    }
  }, [dateFilterMode, quickUploadDateOptions]);

  useEffect(() => {
    if (!viewerOpen || !selectedPhoto) return;
    const idx = filteredPhotos.findIndex((photo) => photo.photo_id === selectedPhoto.photo_id);
    if (idx >= 0) {
      setViewerPhotoIndex(idx);
      return;
    }
    if (filteredPhotos.length > 0) {
      const fallback = filteredPhotos[0];
      setSelectedPhoto(fallback);
      setViewerPhotoIndex(0);
      setViewerEditForm(buildEditFormFromPhoto(fallback));
      setViewerPendingRotationTurns(0);
    } else {
      setViewerOpen(false);
    }
  }, [filteredPhotos, viewerOpen, selectedPhoto]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    // Create preview URLs for thumbnails
    const previews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size
    }));
    setFilePreviews(previews);
  };

  // Cleanup preview URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      filePreviews.forEach(fp => URL.revokeObjectURL(fp.preview));
    };
  }, [filePreviews]);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (!viewerOpen) return;

      const activeElement = document.activeElement;
      const isTypingField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement?.tagName) || activeElement?.isContentEditable;

      if (event.key === 'ArrowRight' && !isTypingField) {
        event.preventDefault();
        goToViewerPhoto('next');
      }
      if (event.key === 'ArrowLeft' && !isTypingField) {
        event.preventDefault();
        goToViewerPhoto('prev');
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [viewerOpen, viewerPhotoIndex, filteredPhotos]);

  const handleOpenEditPhoto = (photo) => {
    setEditingPhoto(photo);
    setPendingRotationTurns(0);
    setEditForm(buildEditFormFromPhoto(photo));
    setEditDialogOpen(true);
  };

  const handleViewPhoto = (photo) => {
    const activePhotoList = filteredPhotos;
    const foundIndex = activePhotoList.findIndex((item) => item.photo_id === photo.photo_id);
    setSelectedPhoto(photo);
    setViewerPhotoIndex(foundIndex >= 0 ? foundIndex : 0);
    setViewerEditForm(buildEditFormFromPhoto(photo));
    setViewerPendingRotationTurns(0);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setViewerOpen(true);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    setZoomLevel(prev => Math.min(Math.max(prev + delta, 0.5), 3));
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsPanning(true);
      setStartPan({
        x: e.clientX - panOffset.x,
        y: e.clientY - panOffset.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && zoomLevel > 1) {
      setPanOffset({
        x: e.clientX - startPan.x,
        y: e.clientY - startPan.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleQueueRotateRight = () => {
    setPendingRotationTurns((prev) => (prev + 1) % 4);
  };

  const handleTogglePatternFilter = (patternId) => {
    setSelectedPatternFilterIds((prev) => {
      if (prev.includes(patternId)) {
        return prev.filter((id) => id !== patternId);
      }
      return [...prev, patternId];
    });
  };

  const handleViewerRotateRight = () => {
    setViewerPendingRotationTurns((prev) => (prev + 1) % 4);
  };

  const goToViewerPhoto = (direction) => {
    if (!viewerOpen || filteredPhotos.length === 0) return;
    const nextIndex = direction === 'next'
      ? (viewerPhotoIndex + 1) % filteredPhotos.length
      : (viewerPhotoIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
    const nextPhoto = filteredPhotos[nextIndex];
    setViewerPhotoIndex(nextIndex);
    setSelectedPhoto(nextPhoto);
    setViewerEditForm(buildEditFormFromPhoto(nextPhoto));
    setViewerPendingRotationTurns(0);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleSaveViewerEdits = async () => {
    if (!selectedPhoto) return;
    try {
      setViewerSaving(true);

      const photoTypeValue = PHOTO_TYPES.find(t => t.label === viewerEditForm.photo_type)?.value || viewerEditForm.photo_type;
      const viewTypeValue = VIEW_TYPES.find(t => t.label === viewerEditForm.view_type)?.value || viewerEditForm.view_type;
      const tagsArray = [];
      if (photoTypeValue && photoTypeValue.trim()) tagsArray.push(photoTypeValue.trim());
      if (viewTypeValue && viewTypeValue.trim()) tagsArray.push(viewTypeValue.trim());
      if (viewerEditForm.date && viewerEditForm.date.trim()) tagsArray.push(viewerEditForm.date.trim());

      await updateFittingPhoto(selectedPhoto.photo_id, {
        description: viewerEditForm.description || '',
        tags: tagsArray,
        pattern_ids: viewerEditForm.pattern_ids || [],
      });

      if (viewerPendingRotationTurns > 0) {
        await rotateFittingPhoto(selectedPhoto.photo_id, {
          quarter_turns: viewerPendingRotationTurns,
        });
      }

      const response = await getFittingPhotos(customerId, null, null);
      const refreshedPhotos = response.data || [];
      setPhotos(refreshedPhotos);

      const updatedIndex = refreshedPhotos.findIndex((photo) => photo.photo_id === selectedPhoto.photo_id);
      if (updatedIndex >= 0) {
        const updatedPhoto = refreshedPhotos[updatedIndex];
        setSelectedPhoto(updatedPhoto);
        setViewerPhotoIndex(updatedIndex);
        setViewerEditForm(buildEditFormFromPhoto(updatedPhoto));
      }
      setViewerPendingRotationTurns(0);
      toast.success('Photo details saved');
    } catch (error) {
      console.error('Error saving in viewer:', error);
      toast.error('Failed to save photo changes');
    } finally {
      setViewerSaving(false);
    }
  };

  const handleSavePhotoEdit = async () => {
    if (!editingPhoto) {
      return;
    }
    
    try {
      setUploading(true);

      // Convert user-friendly labels back to raw values for API
      // If it's a known label, convert to value; otherwise, use as-is (custom value)
      const photoTypeValue = PHOTO_TYPES.find(t => t.label === editForm.photo_type)?.value || editForm.photo_type;
      const viewTypeValue = VIEW_TYPES.find(t => t.label === editForm.view_type)?.value || editForm.view_type;

      // Construct tags from separate fields - include all non-empty values
      const tagsArray = [];
      if (photoTypeValue && photoTypeValue.trim()) tagsArray.push(photoTypeValue.trim());
      if (viewTypeValue && viewTypeValue.trim()) tagsArray.push(viewTypeValue.trim());
      if (editForm.date && editForm.date.trim()) tagsArray.push(editForm.date.trim());

      await updateFittingPhoto(editingPhoto.photo_id, {
        description: editForm.description || '',
        tags: tagsArray,
        pattern_ids: editForm.pattern_ids || [],
      });

      if (pendingRotationTurns > 0) {
        await rotateFittingPhoto(editingPhoto.photo_id, {
          quarter_turns: pendingRotationTurns,
        });
      }

      toast.success('Photo updated successfully');
      setEditDialogOpen(false);
      setEditingPhoto(null);
      setPendingRotationTurns(0);
      await fetchPhotos();
    } catch (error) {
      console.error('Error updating photo:', error);
      toast.error(`Failed to update photo: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await deleteFittingPhoto(photoId);
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    try {
      setUploading(true);
      
      // Upload each file
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('customer_id', customerId);
        formData.append('description', photoForm.description || '');
        
        // Add optional fields
        if ((photoForm.pattern_ids || []).length > 0) {
          formData.append('pattern_id', photoForm.pattern_ids[0]);
          formData.append('pattern_ids', photoForm.pattern_ids.join(','));
        }
        
        // Convert photo metadata to tags - only photo_type and date
        const tags = [photoForm.photo_type, photoForm.date].filter(Boolean).join(',');
        if (tags) {
          formData.append('tags', tags);
        }
        
        return uploadFittingPhoto(formData);
      });
      
      await Promise.all(uploadPromises);
      
      toast.success(`${selectedFiles.length} photo(s) uploaded successfully`);
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setFilePreviews([]);
      setPhotoForm({
        photo_type: 'fitting_session',
        date: new Date().toISOString().split('T')[0],
        description: '',
        pattern_ids: [],
      });
      fetchPhotos(); // Refresh photos
      onRefresh();
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" data-testid="fitting-photos-section-title">Photos</h2>
          <p className="text-sm text-muted-foreground">
            Upload and manage fitting session photos
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)} data-testid="fitting-photos-open-upload-dialog-button">
          <Plus className="w-4 h-4 mr-2" />
          Upload Photos
        </Button>
      </div>

      {photos.length > 0 && (
        <Card data-testid="fitting-photos-pattern-filter-card">
          <CardContent className="py-4">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground mr-1">Pattern filter:</span>
                <Button
                  type="button"
                  size="sm"
                  variant={selectedPatternFilterIds.length === 0 ? 'default' : 'outline'}
                  onClick={() => setSelectedPatternFilterIds([])}
                  data-testid="fitting-photos-filter-all-button"
                >
                  All ({photos.length})
                </Button>
                {patternFilterOptions.map((option) => (
                  <Button
                    type="button"
                    size="sm"
                    key={option.patternId}
                    variant={selectedPatternFilterIds.includes(option.patternId) ? 'default' : 'outline'}
                    onClick={() => handleTogglePatternFilter(option.patternId)}
                    data-testid={`fitting-photos-filter-pattern-${option.patternId}`}
                  >
                    {option.label} ({option.count})
                  </Button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground mr-1">Date filter:</span>
                <Button
                  type="button"
                  size="sm"
                  variant={dateFilterMode === 'custom' ? 'default' : 'outline'}
                  onClick={() => setDateFilterMode('custom')}
                  data-testid="fitting-photos-date-filter-custom-button"
                >
                  Custom
                </Button>
                {quickUploadDateOptions.map((dateValue) => (
                  <Button
                    type="button"
                    size="sm"
                    key={dateValue}
                    variant={dateFilterMode === `date:${dateValue}` ? 'default' : 'outline'}
                    onClick={() => setDateFilterMode(`date:${dateValue}`)}
                    data-testid={`fitting-photos-date-filter-${dateValue}`}
                  >
                    {format(new Date(dateValue), 'MMM d')}
                  </Button>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant={dateFilterMode === 'all' ? 'default' : 'outline'}
                  onClick={() => setDateFilterMode('all')}
                  data-testid="fitting-photos-date-filter-all-button"
                >
                  All Dates
                </Button>
              </div>

              {dateFilterMode === 'custom' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fitting-photos-custom-date-range-controls">
                  <div>
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      data-testid="fitting-photos-custom-date-from-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      data-testid="fitting-photos-custom-date-to-input"
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="fitting-photos-view-controls">
                <span className="text-sm font-medium text-muted-foreground mr-1">Display:</span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={galleryMode === 'grid' ? 'default' : 'outline'}
                    onClick={() => setGalleryMode('grid')}
                    data-testid="fitting-photos-view-grid-button"
                  >
                    <LayoutGrid className="w-4 h-4 mr-1" /> Grid
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={galleryMode === 'list' ? 'default' : 'outline'}
                    onClick={() => setGalleryMode('list')}
                    data-testid="fitting-photos-view-list-button"
                  >
                    <List className="w-4 h-4 mr-1" /> List
                  </Button>
                </div>
                {galleryMode === 'grid' && (
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-muted-foreground">Size:</span>
                    <Button type="button" size="sm" variant={gridDensity === 'small' ? 'default' : 'outline'} onClick={() => setGridDensity('small')} data-testid="fitting-photos-grid-size-small-button">Small</Button>
                    <Button type="button" size="sm" variant={gridDensity === 'medium' ? 'default' : 'outline'} onClick={() => setGridDensity('medium')} data-testid="fitting-photos-grid-size-medium-button">Medium</Button>
                    <Button type="button" size="sm" variant={gridDensity === 'large' ? 'default' : 'outline'} onClick={() => setGridDensity('large')} data-testid="fitting-photos-grid-size-large-button">Large</Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photos Grid */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Loading photos...</p>
          </CardContent>
        </Card>
      ) : photos.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <ImageIcon className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-lg font-medium mb-2">No Photos Yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Upload photos from initial measurements and fitting sessions
              </p>
              <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Photos
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6" data-testid="fitting-photos-grouped-gallery">
          {groupedPhotos.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="fitting-photos-no-filtered-results">
                No photos match the selected filters.
              </CardContent>
            </Card>
          )}

          {groupedPhotos.map((group) => (
            <div key={group.key} className="space-y-3" data-testid={`fitting-photos-group-${group.key}`}>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">Pattern: {group.label}</h3>
                <Badge variant="secondary" data-testid={`fitting-photos-group-count-${group.key}`}>{group.photos.length}</Badge>
              </div>

              {galleryMode === 'grid' ? (
                <div className={gridClassName}>
                  {group.photos.map((photo) => {
                    const tags = photo.tags || [];
                    const viewType = tags.find(t => ['front', 'back', 'side', 'all_sides'].includes(t));
                    const photoDate = extractPhotoDate(photo);
                    const imageUrl = resolvePhotoUrl(photo.thumbnail_uri || photo.file_uri);
                    const associatedPatternCodes = (photo.pattern_codes && photo.pattern_codes.length > 0)
                      ? photo.pattern_codes
                      : getPhotoPatternIds(photo).map((patternId) => patternCodeById[patternId] || patternId);

                    return (
                      <Card key={photo.photo_id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`fitting-photo-card-${photo.photo_id}`}>
                        <CardContent className="p-0">
                          <div className="aspect-square relative bg-muted group cursor-pointer" onClick={() => handleViewPhoto(photo)} data-testid={`fitting-photo-open-viewer-${photo.photo_id}`}>
                            <img
                              src={imageUrl}
                              alt={photo.description || 'Fitting photo'}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                            {viewType && (
                              <div className="absolute top-2 right-2 pointer-events-none">
                                <Badge variant="secondary" className="text-xs capitalize">
                                  {viewType.replace('_', ' ')}
                                </Badge>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 pointer-events-none">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEditPhoto(photo);
                                }}
                                className="pointer-events-auto"
                                data-testid={`fitting-photo-edit-button-${photo.photo_id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePhoto(photo.photo_id);
                                }}
                                className="pointer-events-auto"
                                data-testid={`fitting-photo-delete-button-${photo.photo_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="p-3" data-testid={`fitting-photo-meta-${photo.photo_id}`}>
                            <p className="text-xs text-muted-foreground mb-1">
                              {photoDate && format(new Date(photoDate), 'MMM d, yyyy')}
                            </p>
                            {photo.description && (
                              <p className="text-sm line-clamp-2 mb-2">{photo.description}</p>
                            )}
                            {photo.file_size && (
                              <p className="text-xs text-muted-foreground">
                                {Math.round(photo.file_size / 1024)} KB
                              </p>
                            )}
                            {associatedPatternCodes.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1" data-testid={`fitting-photo-pattern-badges-${photo.photo_id}`}>
                                {associatedPatternCodes.map((code, index) => (
                                  <Badge key={`${photo.photo_id}-pattern-${index}`} variant="outline" className="text-xs">
                                    {code}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-3" data-testid={`fitting-photos-list-group-${group.key}`}>
                  {group.photos.map((photo) => {
                    const photoDate = extractPhotoDate(photo);
                    const imageUrl = resolvePhotoUrl(photo.thumbnail_uri || photo.file_uri);
                    const associatedPatternCodes = (photo.pattern_codes && photo.pattern_codes.length > 0)
                      ? photo.pattern_codes
                      : getPhotoPatternIds(photo).map((patternId) => patternCodeById[patternId] || patternId);

                    return (
                      <Card key={photo.photo_id} className="hover:shadow-md transition-shadow" data-testid={`fitting-photo-list-row-${photo.photo_id}`}>
                        <CardContent className="p-3 flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => handleViewPhoto(photo)}
                            className="w-20 h-20 rounded-md overflow-hidden shrink-0"
                            data-testid={`fitting-photo-list-open-viewer-${photo.photo_id}`}
                          >
                            <img src={imageUrl} alt={photo.description || 'Fitting photo'} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{photo.description || 'No notes added'}</p>
                            <p className="text-xs text-muted-foreground">{photoDate ? format(new Date(photoDate), 'MMM d, yyyy') : '—'}</p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {associatedPatternCodes.map((code, index) => (
                                <Badge key={`${photo.photo_id}-list-pattern-${index}`} variant="outline" className="text-xs">{code}</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleOpenEditPhoto(photo)} data-testid={`fitting-photo-list-edit-button-${photo.photo_id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeletePhoto(photo.photo_id)} data-testid={`fitting-photo-list-delete-button-${photo.photo_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Photos</DialogTitle>
            <DialogDescription>
              Add photos from initial measurements or fitting sessions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File Input */}
            <div>
              <Label>Select Photos</Label>
              <div className="mt-2">
                {filePreviews.length === 0 ? (
                  <label className="flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium">Click to select photos</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Mobile-optimized • Auto-compressed
                      </p>
                    </div>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-2">
                      {filePreviews.map((fp, idx) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border">
                          <img 
                            src={fp.preview} 
                            alt={fp.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 p-1">
                            <p className="text-xs text-white truncate">{fp.name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <label className="flex items-center justify-center w-full h-10 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      <p className="text-sm">Add more photos</p>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files);
                          const newPreviews = newFiles.map(file => ({
                            file,
                            preview: URL.createObjectURL(file),
                            name: file.name,
                            size: file.size
                          }));
                          setSelectedFiles([...selectedFiles, ...newFiles]);
                          setFilePreviews([...filePreviews, ...newPreviews]);
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Photo Type</Label>
                <Select
                  value={photoForm.photo_type}
                  onValueChange={(value) => setPhotoForm({ ...photoForm, photo_type: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PHOTO_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={photoForm.date}
                  onChange={(e) => setPhotoForm({ ...photoForm, date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {photoForm.photo_type === 'fitting_session' && (
              <div>
                <Label>Associated Pattern Numbers</Label>
                <div className="mt-2 max-h-40 overflow-auto rounded-md border p-3 space-y-2" data-testid="fitting-photo-upload-pattern-multiselect">
                  {patterns.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No patterns available</p>
                  ) : (
                    patterns.map((pattern) => {
                      const checked = (photoForm.pattern_ids || []).includes(pattern.pattern_id);
                      return (
                        <label
                          key={pattern.pattern_id}
                          className="flex items-center gap-2 text-sm"
                          data-testid={`fitting-photo-upload-pattern-option-${pattern.pattern_id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(nextChecked) => {
                              setPhotoForm((prev) => {
                                const existing = prev.pattern_ids || [];
                                const updated = nextChecked
                                  ? [...existing, pattern.pattern_id]
                                  : existing.filter((id) => id !== pattern.pattern_id);
                                return { ...prev, pattern_ids: updated };
                              });
                            }}
                          />
                          <span>{pattern.pattern_code}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="Add notes about these photos..."
                value={photoForm.description}
                onChange={(e) => setPhotoForm({ ...photoForm, description: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setSelectedFiles([]);
                setFilePreviews([]);
                setPhotoForm({
                  photo_type: 'fitting_session',
                  date: new Date().toISOString().split('T')[0],
                  description: '',
                  pattern_ids: [],
                });
              }}
              disabled={uploading}
              data-testid="fitting-photo-upload-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || selectedFiles.length === 0} data-testid="fitting-photo-upload-submit-button">
              {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Photo Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Photo Details</DialogTitle>
            <DialogDescription>
              Update description and tags for this photo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingPhoto && (
              <div className="rounded-md border p-3 space-y-3" data-testid="fitting-photo-edit-preview-container">
                <div className="text-sm font-medium">Rotation Preview</div>
                <div className="w-full max-h-56 overflow-hidden rounded-md bg-muted flex items-center justify-center">
                  <img
                    src={resolvePhotoUrl(editingPhoto.thumbnail_uri || editingPhoto.file_uri)}
                    alt="Rotation preview"
                    className="max-h-56 object-contain transition-transform duration-150"
                    style={{ transform: `rotate(${pendingRotationTurns * 90}deg)` }}
                    data-testid="fitting-photo-edit-rotation-preview-image"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleQueueRotateRight}
                    data-testid="fitting-photo-edit-rotate-right-button"
                  >
                    <RotateCw className="w-4 h-4 mr-1" />
                    Rotate Right
                  </Button>
                  <span className="text-xs text-muted-foreground" data-testid="fitting-photo-edit-rotation-turns-indicator">
                    Pending: {pendingRotationTurns * 90}°
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Photo Type</Label>
                <Combobox
                  options={PHOTO_TYPES.map(t => t.label)}
                  value={editForm.photo_type}
                  onValueChange={(label) => {
                    setEditForm({ ...editForm, photo_type: label });
                  }}
                  placeholder="Select photo type"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>View Type</Label>
                <Combobox
                  options={VIEW_TYPES.map(t => t.label)}
                  value={editForm.view_type}
                  onValueChange={(label) => {
                    setEditForm({ ...editForm, view_type: label });
                  }}
                  placeholder="Select view type"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Associated Pattern Numbers</Label>
              <div className="mt-2 max-h-40 overflow-auto rounded-md border p-3 space-y-2" data-testid="fitting-photo-edit-pattern-multiselect">
                {patterns.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No patterns available</p>
                ) : (
                  patterns.map((pattern) => {
                    const checked = (editForm.pattern_ids || []).includes(pattern.pattern_id);
                    return (
                      <label
                        key={pattern.pattern_id}
                        className="flex items-center gap-2 text-sm"
                        data-testid={`fitting-photo-edit-pattern-option-${pattern.pattern_id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(nextChecked) => {
                            setEditForm((prev) => {
                              const existing = prev.pattern_ids || [];
                              const updated = nextChecked
                                ? [...existing, pattern.pattern_id]
                                : existing.filter((id) => id !== pattern.pattern_id);
                              return { ...prev, pattern_ids: updated };
                            });
                          }}
                        />
                        <span>{pattern.pattern_code}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div>
              <Label>Description / Notes</Label>
              <Textarea
                placeholder="Add notes about this photo..."
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false);
                setEditingPhoto(null);
                setPendingRotationTurns(0);
              }}
              data-testid="fitting-photo-edit-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={handleSavePhotoEdit} disabled={uploading} data-testid="fitting-photo-edit-save-button">
              {uploading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Viewer Dialog with Zoom */}
      <Dialog
        open={viewerOpen}
        onOpenChange={(open) => {
          setViewerOpen(open);
          if (!open) {
            setViewerPendingRotationTurns(0);
            setZoomLevel(1);
            setPanOffset({ x: 0, y: 0 });
          }
        }}
      >
        <DialogContent className="max-w-[98vw] max-h-[98vh] p-0">
          <div className="relative w-full h-[95vh] bg-black flex">
            {/* Main photo area */}
            <div 
              className="flex-1 overflow-hidden flex items-center justify-center relative"
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
            >
              {selectedPhoto && (
                <div 
                  className="relative flex items-center justify-center w-full h-full"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                    transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                  }}
                >
                  <img
                    src={resolvePhotoUrl(selectedPhoto.file_uri)}
                    alt={selectedPhoto.description || 'Fitting photo'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      transform: `rotate(${viewerPendingRotationTurns * 90}deg)`,
                    }}
                    draggable={false}
                  />
                </div>
              )}

              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={() => goToViewerPhoto('prev')}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10"
                data-testid="fitting-photo-viewer-previous-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onClick={() => goToViewerPhoto('next')}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10"
                data-testid="fitting-photo-viewer-next-button"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
              
              {/* Zoom hint overlay */}
              {zoomLevel === 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full pointer-events-none">
                  Scroll to zoom • Click and drag when zoomed
                </div>
              )}
            </div>

            {/* Right sidebar with controls and info */}
            <div className="w-80 bg-gray-900 flex flex-col">
              {/* Close button at top */}
              <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                <h3 className="text-white font-semibold">Photo Details</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewerOpen(false)}
                  className="text-white hover:bg-gray-800"
                >
                  <CloseIcon className="w-5 h-5" />
                </Button>
              </div>

              {/* Photo metadata */}
              {selectedPhoto && (
                <div className="p-4 border-b border-gray-700 space-y-4" data-testid="fitting-photo-viewer-edit-panel">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400">Photo {viewerPhotoIndex + 1} of {filteredPhotos.length}</p>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-7 w-7 bg-gray-800 border-gray-700 text-white hover:bg-gray-700" onClick={() => goToViewerPhoto('prev')} data-testid="fitting-photo-viewer-prev-inline-button">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="outline" className="h-7 w-7 bg-gray-800 border-gray-700 text-white hover:bg-gray-700" onClick={() => goToViewerPhoto('next')} data-testid="fitting-photo-viewer-next-inline-button">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-400 text-xs">Photo Type</Label>
                    <Input
                      value={viewerEditForm.photo_type}
                      onChange={(e) => setViewerEditForm((prev) => ({ ...prev, photo_type: e.target.value }))}
                      className="mt-1 bg-gray-800 border-gray-700 text-white"
                      data-testid="fitting-photo-viewer-photo-type-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400 text-xs">View Type</Label>
                    <Input
                      value={viewerEditForm.view_type}
                      onChange={(e) => setViewerEditForm((prev) => ({ ...prev, view_type: e.target.value }))}
                      className="mt-1 bg-gray-800 border-gray-700 text-white"
                      data-testid="fitting-photo-viewer-view-type-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400 text-xs">Date</Label>
                    <Input
                      type="date"
                      value={viewerEditForm.date}
                      onChange={(e) => setViewerEditForm((prev) => ({ ...prev, date: e.target.value }))}
                      className="mt-1 bg-gray-800 border-gray-700 text-white"
                      data-testid="fitting-photo-viewer-date-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400 text-xs">Pattern Nos</Label>
                    <div className="mt-2 max-h-28 overflow-auto rounded-md border border-gray-700 p-2 space-y-1">
                      {patterns.map((pattern) => {
                        const checked = (viewerEditForm.pattern_ids || []).includes(pattern.pattern_id);
                        return (
                          <label key={pattern.pattern_id} className="flex items-center gap-2 text-xs text-white" data-testid={`fitting-photo-viewer-pattern-option-${pattern.pattern_id}`}>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(nextChecked) => {
                                setViewerEditForm((prev) => {
                                  const existing = prev.pattern_ids || [];
                                  const updated = nextChecked
                                    ? [...existing, pattern.pattern_id]
                                    : existing.filter((id) => id !== pattern.pattern_id);
                                  return { ...prev, pattern_ids: updated };
                                });
                              }}
                            />
                            <span>{pattern.pattern_code}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-400 text-xs">Notes</Label>
                    <Textarea
                      value={viewerEditForm.description}
                      onChange={(e) => setViewerEditForm((prev) => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 bg-gray-800 border-gray-700 text-white"
                      data-testid="fitting-photo-viewer-notes-textarea"
                    />
                  </div>

                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleViewerRotateRight}
                      className="w-full bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                      data-testid="fitting-photo-viewer-rotate-right-button"
                    >
                      <RotateCw className="w-4 h-4 mr-2" />
                      Rotate Right ({viewerPendingRotationTurns * 90}° pending)
                    </Button>

                    <Button
                      type="button"
                      onClick={handleSaveViewerEdits}
                      disabled={viewerSaving}
                      className="w-full"
                      data-testid="fitting-photo-viewer-save-button"
                    >
                      {viewerSaving ? 'Saving...' : 'Save Photo Changes'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Zoom controls */}
              <div className="p-4 space-y-4">
                <div>
                  <Label className="text-gray-400 text-xs mb-2 block">Zoom Level</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.5}
                      className="flex-1 bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                    >
                      <ZoomOut className="w-4 h-4 mr-1" />
                      Out
                    </Button>
                    <div className="w-16 text-center">
                      <span className="text-white font-medium text-sm">
                        {Math.round(zoomLevel * 100)}%
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 3}
                      className="flex-1 bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                    >
                      <ZoomIn className="w-4 h-4 mr-1" />
                      In
                    </Button>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={handleResetZoom}
                  className="w-full bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                >
                  Reset View
                </Button>
              </div>

              {/* Instructions */}
              <div className="mt-auto p-4 border-t border-gray-700">
                <div className="text-gray-400 text-xs space-y-2">
                  <p className="flex items-center gap-2">
                    <span className="font-semibold text-white">Mouse Wheel:</span>
                    Zoom in/out
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold text-white">Click & Drag:</span>
                    Pan when zoomed
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold text-white">← / → Keys:</span>
                    Previous / next photo
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotosTab;